import { useAxios } from "@utils/hooks/useAxios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { filterObject } from "@/utils/helpers";
import { useNavigate, useParams, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { produce } from "immer";
import { useSystem } from "@/utils/hooks/useLocation";

// ==============================
// Queries
// ==============================

export const useHouseExaminationItemsQuery = ({ params = {} } = {}) => {
  const { axiosProtected } = useAxios();

  const filters = filterObject(params ?? {});

  return useQuery({
    queryKey: ["house-examination-items", filters],
    queryFn: () =>
      axiosProtected.get("makkah/house-examination-items", { params: filters }),
  });
};

export const usePeriodCodeDetails = (period_code) => {
  const { axiosProtected } = useAxios();
  return useQuery({
    queryKey: ["period-code-details", period_code],
    queryFn: () =>
      axiosProtected.get(`makkah/period-codes/${period_code}/details`),

    enabled: !!period_code,
  });
};

export const useAllHousingInspectionQuery = (params) => {
  const { axiosProtected } = useAxios();
  const filters = filterObject(params ?? {});
  const { currentSystem } = useSystem();

  return useQuery({
    queryKey: ["all-house-inspections", filters],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-examinations`, {
        params: filters,
      }),
  });
};

export const useAllAssignedInspectionUsers = (params) => {
  const { axiosProtected } = useAxios();
  const filters = filterObject(params ?? {});
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["all-assigned-inspection-users", filters],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-examinations/users`, {
        params: filters,
      }),
  });
};
export const useOneHousingInspectionOrder = () => {
  const { axiosProtected } = useAxios();
  const { order_number } = useParams();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-housing-inspection-order", order_number],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-examinations/orders/${order_number}`),
    enabled: !!order_number,
  });
};

export const useAllInsepctionOrders = (params) => {
  const { axiosProtected } = useAxios();
  const filters = filterObject(params ?? {});
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["all-inspection-orders", filters],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-examinations/orders`, {
        params: filters,
      }),
  });
};
export const useAllInsepctionTracks = (params) => {
  const { axiosProtected } = useAxios();
  const filters = filterObject(params ?? {});
  const { currentSystem } = useSystem();
  
  return useQuery({
    queryKey: ["all-inspection-tracks", filters],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-examinations/track-cards`, {
        params: filters,
      }),
  });
};
export const useOneOrderQuery = () => {
  const { axiosProtected } = useAxios();
  const { order_number } = useParams();
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-order", order_number],
    queryFn: () =>
      axiosProtected.get(`${currentSystem}/house-examinations/orders/${order_number}`),
    enabled: !!order_number,
  });
};

export const useOneBuildingItemsQuery = ({ enabled = true }) => {
  const { axiosProtected } = useAxios();
  const { order_number, house_id } = useParams();
  const [hasResult] = useSearchParams();
  const has_result = hasResult.get("has_result");
  const include_images = Boolean(has_result);
  const { currentSystem } = useSystem();
  return useQuery({
    queryKey: ["one-building-items", order_number, house_id],
    queryFn: () =>
      axiosProtected.get(
        `${currentSystem}/house-examinations/orders/${order_number}/houses/${house_id}/items`,
        {
          params: {
            include_images,
          },
        }
      ),
    enabled: !!enabled,
  });
};
export const useSearchHousesQuery = (params) => {
  const { axiosProtected } = useAxios();
  const paramsFiltered = filterObject(params ?? {});

  return useQuery({
    queryKey: ["search-houses"],
    queryFn: () =>
      axiosProtected.get("makkah/house-contracts/houses/search", {
        params: paramsFiltered,
      }),
    enabled: !!paramsFiltered.houseComrRegNo,
  });
};

// ==============================
// Mutations
// ==============================
export const useCreateHousingInspectionOrder = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(`${currentSystem}/house-examinations/orders`, data),
    onSuccess: () => {
      toast.success(t("HOUSING_INSPECTION_ORDER_CREATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({
        queryKey: ["all-inspection-orders"],
      });
    },
  });
};

export const useEditHousingInspectionOrder = () => {
  const { axiosProtected } = useAxios();
  const { house_contract_id, houseComrRegNo, order_number } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: (data) =>
      axiosProtected.post(
        `${currentSystem}/house-examinations/orders/${order_number}`,
        data
      ),

    onSuccess: () => {
      toast.success(t("HOUSING_INSPECTION_ORDER_UPDATED_SUCCESSFULLY"));
      navigate(
        `/app/makkah/housing-inspection-orders/${house_contract_id}/${houseComrRegNo}/${order_number}/view`
      );
    },
  });
};
export const useUpdateHousingInspectionOrderStatus = (params) => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ order_number, ...payload }) =>
      axiosProtected.post(
        `${currentSystem}/house-examinations/orders/${order_number}/status`,
        payload
      ),

    onSuccess: ($, { order_number }) => {
      toast.success(t("HOUSING_INSPECTION_ORDER_UPDATED_SUCCESSFULLY"));
      queryClient.invalidateQueries({
        queryKey: ["all-inspection-orders", params],
      });
      navigate(
        `/app/${currentSystem}/housing-inspection-orders/${order_number}/buildings`
      );
    },
    onError: (error) => {
      toast.error(t("HOUSING_INSPECTION_ORDER_UPDATED_FAILED"));
    },
  });
};

export const useUpdateOrderItemsMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { order_number, house_id } = useParams();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ formData, include_images }) =>
      axiosProtected.post(
        `${currentSystem}/house-examinations/orders/${order_number}/houses/${house_id}/items`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          params: {
            include_images,
          },
        }
      ),
    onSuccess: (data) => {
      toast.success(t("HOUSING_INSPECTION_ORDER_ITEMS_UPDATED_SUCCESSFULLY"));
      queryClient.setQueryData(
        ["one-building-items", order_number, house_id],
        (old) => {
          return produce(old, (draft) => {
            draft.data.data.items = data.data.data.items;
            draft.data.data.examination_result =
              data.data.data.examination_result;
          });
        }
      );
    },
  });
};

export const useUploadHouseExaminationItemMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { order_number, house_id } = useParams();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ item_id, ...data }) => {
      const formData = new FormData();
      data?.images?.forEach((image) => {
        formData.append("images[]", image);
      });

      return axiosProtected.post(
        `${currentSystem}/house-examinations/orders/${order_number}/houses/${house_id}/items/${item_id}/images`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
    },

    onSuccess: () => {
      toast.success(t("IMAGES_UPLOADED_SUCCESSFULLY"));
      // queryClient.setQueryData(
      //   ["one-building-items", order_number, house_id],
      //   (old) => {
      //     return produce(old, (draft) => {
      //       const { item_id } = variables;
      //       const items = [...draft.data.data.items];
      //       const newItemsState = items.map((item) =>
      //         Number(item.id) === Number(item_id) ? { ...item, images } : item
      //       );

      //       draft.data.data.items = newItemsState;
      //     });
      //   }
      // );
    },
    onError: (error) => {
      toast.error(t("IMAGES_UPLOAD_FAILED"));
    },
  });
};
export const useDeleteHouseExaminationItemImageMutation = () => {
  const { axiosProtected } = useAxios();
  const { t } = useTranslation();
  const { order_number, house_id } = useParams();
  const { currentSystem } = useSystem();
  return useMutation({
    mutationFn: ({ item_id, id }) => {
      return axiosProtected.delete(
        `${currentSystem}/house-examinations/orders/${order_number}/houses/${house_id}/items/${item_id}/images/${id}`
      );
    },

    onSuccess: () => {
      toast.success(t("IMAGE_DELETED_SUCCESSFULLY"));
    },
    onError: (error) => {
      toast.error(t("IMAGE_DELETED_FAILED"));
    },
  });
};
